import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { BrowserRouter, Routes, Route } from 'react-router'
import { useEffect } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'

// Pages
import HomePage from './pages/home.page'
import SuccessPage from './pages/success.page'
import ComingSoonPage from './pages/coming-soon.page'

// Layout
import MobileLayout from './core/layout/mobile.layout.core'

import { setLanguagesFromCountryAtom, availableLanguagesAtom } from './core/store/language.store'
import type { LanguageItem, LanguageSettings } from './core/store/language.store'
import Theme from './core/theme/theme'
import { FontLoader } from '@repo/fonts'
import { isLoadingCountriesAtom, useCountries } from '@repo/unicity'
import { countriesAtom } from '@repo/unicity'
import OrderDetailPage from './pages/order-detail.page'
import { LANGUAGES } from './core/constants/languages'
import { useGlicDictionary } from './core/hooks/useDictionary'
import LoaderTracking from './core/loader/components/LoaderTracking.component'
import InAppSpy from 'inapp-spy'

function App() {
    const { isInApp } = InAppSpy()
    // Silent employee login on app start
    const countries = useAtomValue(countriesAtom)
    const isLoadingCountries = useAtomValue(isLoadingCountriesAtom)
    const { fetchCountries } = useCountries()
    const setLanguagesFromCountry = useSetAtom(setLanguagesFromCountryAtom)

    // Initialize glic2027 dictionary (handles language detection and dictionary fetching)
    useGlicDictionary()
    const availableLanguages = useAtomValue(availableLanguagesAtom)

    function openInBrowser() {
        const url = 'https://glic.unicity.com'

        // Detect if the user is on Android
        const isAndroid = /Android/i.test(navigator.userAgent)

        // Get the current URL (to avoid infinite redirects)
        const currentUrl = window.location.href

        // Check if we are already on localhost or if it is the intent URL
        const isLocalhost = currentUrl.startsWith('http://localhost')
        const isIntentUrl = currentUrl.startsWith('intent://')

        // If not localhost or already on an intent URL, proceed with redirection
        if (isAndroid && !isLocalhost && !isIntentUrl) {
            // Use the intent URL scheme for Android
            const intentUrl = 'intent://' + url.replace(/^https?:\/\//, '') + '#Intent;scheme=https;end'
            window.location.href = intentUrl
            setTimeout(() => {
                // Try to close the in-app browser tab
                window.close()
                // Fallback: redirect if close doesn't work
                window.location.href = 'https://unicity.com'
            }, 1500)
        }
    }

    useEffect(() => {
        // Step 1: Fetch countries if not loaded yet (for other purposes)
        if (!countries?.data) {
            if (!isLoadingCountries) {
                fetchCountries()
            }
        }

        // Step 2: Skip if languages already loaded
        if (availableLanguages.length > 0) {
            return
        }

        // Step 3: Use all 5 languages from LANGUAGES constant directly
        // This ensures all supported languages are always available
        const languageItems: LanguageItem[] = LANGUAGES.map(lang => ({
            code: lang.code.toUpperCase(),
            locale: `${lang.code}-${lang.code.toUpperCase()}`, // e.g., en-EN, th-TH
            name: lang.name
        }))

        // Build language settings with EN as default
        const languages: LanguageSettings = {
            default: 'EN',
            items: languageItems
        }

        console.log('Available Languages:', languages)
        setLanguagesFromCountry(languages)
    }, [countries, availableLanguages.length, fetchCountries, isLoadingCountries, setLanguagesFromCountry])

    useEffect(() => {
        if (isInApp) {
            openInBrowser()
        }
    }, [isInApp])

    return (
        <ThemeProvider theme={Theme}>
            <CssBaseline />
            <LoaderTracking />
            <BrowserRouter basename="/">
                <FontLoader fonts={['inter', 'poppins']} />
                <Routes>
                    {/* Coming Soon page - no header/footer */}
                    <Route path="/comingsoon" element={<ComingSoonPage />} />

                    {/* Main pages with MobileLayout (header/footer) */}
                    <Route element={<MobileLayout />}>
                        <Route path="/" element={<HomePage />} />
                        <Route path="/order-detail" element={<OrderDetailPage />} />
                        <Route path="/success/:responseCode?" element={<SuccessPage />} />
                        <Route path="/failed" element={<SuccessPage />} />
                    </Route>
                </Routes>
            </BrowserRouter>
        </ThemeProvider>
    )
}

export default App
